import React, { useEffect, useState } from "react";
import { Modal, notification } from "antd";
import {
  IoMailOutline,
  IoLockClosedOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoShieldCheckmarkOutline,
} from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../utils/config";
import logo from '../../assests/images/weblogo.png';

export default function Login() {
  const [userData, setUserData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataLocal = localStorage.getItem("user");
  const timeStamp = localStorage.getItem("timestamp");
  const user = userDataLocal ? JSON.parse(userDataLocal) : null;

  useEffect(() => {
    if (timeStamp && Number(timeStamp) < Date.now()) {
      localStorage.removeItem("token");
      localStorage.removeItem("timestamp");
      setUserData((prev) => ({ ...prev, password: "" }));
      navigate("/login");
      return;
    }
    if (token && user) {
      if (user?.type === "admin" || user?.type === "agent") {
        navigate("/admin");
      } else if (user?.type === "user") {
        navigate("/user");
      }
    }
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const { data } = await axios.post(`${BASE_URL}login`, { ...userData });

      if (!data) throw new Error("Invalid response from server");

      const token = data?.authorization?.token;
      const user = data?.user;

      if (token) localStorage.setItem("token", token);
      if (user) localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("timestamp", Date.now() + 3600 * 1000);
      localStorage.setItem("time", Date.now());

      notification.success({ message: "Login successful" });
      setUserData({ email: "", password: "" });

      if (["admin", "agent"].includes(user?.type)) {
        navigate("/admin");
      } else {
        navigate("/user");
      }
    } catch (error) {
      const errors = error?.response?.data?.errors;
      const message = error?.response?.data?.message;

      if (errors?.email) {
        notification.error({ message: errors.email[0] });
      } else if (errors?.password) {
        notification.error({ message: errors.password[0] });
      } else if (message) {
        notification.error({ message });
      } else {
        notification.error({ message: "Login failed. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const checks = [
    "Account details validity",
    "Login source verification",
    "Quick threat scan",
  ];

  const features = [
    "Secure multi-wallet management",
    "Real-time balance tracking",
    "Instant crypto & fiat conversions",
  ];

  if (token && user) return null;

  return (
    <div className="min-h-screen flex bg-[#080810]">

      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex w-[46%] min-h-screen relative flex-col p-10 gap-10 overflow-hidden bg-[#0c0b18]">

        {/* Grid bg */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(109,40,217,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(109,40,217,0.07) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 90% 55% at 50% -5%, rgba(109,40,217,0.38) 0%, transparent 70%), radial-gradient(ellipse 60% 70% at 90% 90%, rgba(192,38,211,0.18) 0%, transparent 60%)",
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-2.5">
          <Link to="/">
            <img src={logo} alt="Ellipticline Logo" className="h-11 w-auto object-contain" />
          </Link>
        </div>

        {/* Body */}
        <div className="relative z-10 flex flex-col gap-8 flex-1 justify-center">

          <div className="flex flex-col gap-3">
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.08] tracking-tight">
              Welcome{" "}
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                back.
              </span>
            </h1>
            <p className="text-sm text-white/80 leading-relaxed max-w-sm">
              Sign in to access your portfolio, manage your wallets, and stay on top of every market move.
            </p>
          </div>

          {/* Features */}
          <div className="flex flex-col gap-3">
            {features.map((f) => (
              <div key={f} className="flex items-center gap-3 text-white/50 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:border-l lg:border-white/5">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
         <Link to="/">
                     <img src={logo} alt="Ellipticline Logo" className="h-11 w-auto object-contain" />
                   </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/25 text-violet-400 text-[11px] font-medium tracking-wider px-3 py-1 rounded-full mb-4">
              <IoShieldCheckmarkOutline />
              Secure login
            </div>
            <h2 className="text-[2rem] font-extrabold text-white tracking-tight leading-tight mb-1">
              Sign in
            </h2>
            <p className="text-sm text-white/80">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-violet-400 font-semibold hover:text-violet-300 transition-colors"
              >
                Create one free
              </Link>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="flex flex-col gap-4" autoComplete="off">

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium text-white tracking-wide">
                Email address
              </label>
              <div className="relative">
                <IoMailOutline className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 text-[15px] pointer-events-none" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin(e)}
                  className="w-full h-11 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm pl-10 pr-4 outline-none placeholder:text-white/20 focus:border-violet-500/60 focus:bg-violet-500/[0.06] transition-all duration-200"
                />
              </div>
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[12px] font-medium text-white tracking-wide">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[12px] text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <IoLockClosedOutline className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 text-[15px] pointer-events-none" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={userData.password}
                  onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin(e)}
                  className="w-full h-11 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm pl-10 pr-11 outline-none placeholder:text-white/20 focus:border-violet-500/60 focus:bg-violet-500/[0.06] transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60 transition-colors cursor-pointer"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <IoEyeOffOutline className="text-[15px]" />
                    : <IoEyeOutline className="text-[15px]" />
                  }
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !userData.email || !userData.password}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2.5 mt-2 hover:opacity-90 active:scale-[0.985] transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {loading ? "Signing in…" : "Sign in"}
            </button>

          </form>

          {/* Footer */}
          <p className="text-center text-sm text-white mt-6">
            New to Elipticline?{" "}
            <Link
              to="/signup"
              className="text-violet-400 font-semibold hover:text-violet-300 transition-colors"
            >
              Create a free account
            </Link>
          </p>

        </div>
      </div>

      {/* ── Verification Modal ── */}
      <Modal
        open={loading}
        footer={null}
        closable={false}
        width={440}
        centered
        styles={{
          content: {
            background: "#0f0e1c",
            borderRadius: 16,
            padding: 0,
            border: "1px solid rgba(255,255,255,0.07)",
          },
          mask: {
            backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.7)",
          },
        }}
      >
        <div className="p-8 text-center">

          {/* Spinner */}
          <div className="w-14 h-14 rounded-full border-[3px] border-violet-500/15 border-t-violet-500 animate-spin mx-auto mb-6" />

          <h3 className="text-lg font-bold text-white mb-1 tracking-tight">
            Verifying your details
          </h3>
          <p className="text-sm text-white/35 mb-7">
            Running security checks — just a moment.
          </p>

          <div className="flex flex-col gap-2.5">
            {checks.map((label, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]"
              >
                <div className="w-7 h-7 rounded-full bg-violet-500/15 border border-violet-500/25 flex items-center justify-center flex-shrink-0">
                  <span className="text-[11px] font-bold text-violet-400">{i + 1}</span>
                </div>
                <span className="text-[13px] text-white/50 flex-1 text-left">{label}</span>
                <span className="text-[11px] text-white/20 uppercase tracking-wider">Pending</span>
              </div>
            ))}
          </div>

        </div>
      </Modal>

    </div>
  );
}